import * as React from 'react';
import {connect} from 'react-redux';

import moment from 'moment';
import type Moment from 'moment';

import {jsonToModelPropArray} from 'nutshell-core';

import {isToday, isTomorrow, isYesterday} from 'nutshell-core/date-time';

// @ts-expect-error
import {WEEK} from 'shells/calendar/calendar-constants';
import {CalendarDates} from 'shells/calendar/calendar-dates';
import {DayOfWeekBar, ONE_DIGIT_DAY_FORMAT} from 'shells/calendar/day-of-week-bar';
import {ChevronLeftIcon, ChevronRightIcon} from 'shells/icon';

import type {NutshellState} from '../../store';

import {ACTIVITIES} from '../master-dashboard/dashboard-constants';
import type {ActivitiesTabKey, TodosTabKey} from '../master-dashboard/dashboard-constants';
import {DashboardTabs} from '../master-dashboard/panes/dashboard-tabs';

import {updateSidebarCalendarActiveView, updateSidebarCalendarDisplayDate} from './sidebar-actions';
import {SidebarCalendarActivities} from './sidebar-calendar-activities';
import {SidebarCalendarTodos} from './sidebar-calendar-todos';
import {SidebarDrawer} from './sidebar-drawer';
import {
    getSidebarCalendarActiveView,
    getSidebarCalendarMomentDisplayDate,
} from './sidebar-selectors';

import styles from './sidebar-calendar.module.css';

const mapStateToProps = (state: NutshellState): StateProps => {
    return {
        activeView: getSidebarCalendarActiveView(state),
        momentDisplayDate: getSidebarCalendarMomentDisplayDate(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onControlsChange: (activeView) => {
            dispatch(updateSidebarCalendarActiveView(activeView));
        },
        onDisplayDateChange: (newDateTimestamp) => {
            dispatch(updateSidebarCalendarDisplayDate(newDateTimestamp));
        },
    };
};

type OwnProps = {
    userId: string;
    closeSidebar: () => void;
};

type StateProps = {
    // @ts-expect-error
    momentDisplayDate: Moment;
    activeView: ActivitiesTabKey | TodosTabKey;
};

type DispatchProps = {
    onDisplayDateChange: (newDateTimestamp: number) => any;
    onControlsChange: (activeView: ActivitiesTabKey | TodosTabKey) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
    todos: any[];
    todosHasError: boolean;
    isLoadingTodos: boolean;
};

export class SidebarCalendarComponent extends React.PureComponent<Props, State> {
    componentDidMount() {
        this.getTodosForDay(this.props.momentDisplayDate);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (this.props.momentDisplayDate !== nextProps.momentDisplayDate) {
            this.getTodosForDay(nextProps.momentDisplayDate);
        }
    }

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            todos: [],
            todosHasError: false,
            isLoadingTodos: true,
        };
    }

    render() {
        return (
            <SidebarDrawer customHeader={this.getCustomCalendarHeader()}>
                <div className={styles['sidebar-calendar']}>
                    <div className={styles['calendar-dates']}>
                        <div className={styles['day-of-week-bar-container']}>
                            <DayOfWeekBar
                                displayMode={WEEK}
                                customContainerStyles={{backgroundColor: '#f3f3f3'}}
                                dateFormat={ONE_DIGIT_DAY_FORMAT}
                            />
                        </div>
                        <CalendarDates
                            displayMode={WEEK}
                            alwaysStartOnSunday={true}
                            onDateClick={this.handleDisplayDateChange}
                            onDateMouseOut={() => {}}
                            onDateMouseOver={() => {}}
                            selectedDates={[this.props.momentDisplayDate]}
                            quickViews={{}}
                            hasRoundedDates={true}
                            showAdjacentMonthDates={true}
                            momentDisplayDate={this.props.momentDisplayDate}
                        />
                    </div>
                    <div className={styles.controls}>
                        <DashboardTabs
                            name='calendarControls'
                            activeView={this.props.activeView}
                            onChange={this.props.onControlsChange}
                        />
                    </div>
                    <div className={styles.list}>
                        {this.props.activeView === ACTIVITIES ? (
                            <SidebarCalendarActivities
                                userId={this.props.userId}
                                momentDisplayDate={this.props.momentDisplayDate}
                                closeSidebar={this.props.closeSidebar}
                            />
                        ) : (
                            <SidebarCalendarTodos
                                todos={this.state.todos}
                                refetchTodos={() =>
                                    this.getTodosForDay(this.props.momentDisplayDate)
                                }
                                hasError={this.state.todosHasError}
                                isLoading={this.state.isLoadingTodos}
                            />
                        )}
                    </div>
                </div>
            </SidebarDrawer>
        );
    }

    getCustomCalendarHeader = () => {
        let formattedDate = this.props.momentDisplayDate.format('MMMM Do');

        if (isToday(this.props.momentDisplayDate, moment())) {
            formattedDate = 'Today';
        } else if (isTomorrow(this.props.momentDisplayDate, moment())) {
            formattedDate = 'Tomorrow';
        } else if (isYesterday(this.props.momentDisplayDate, moment())) {
            formattedDate = 'Yesterday';
        } else if (!this.props.momentDisplayDate.isSame(moment(), 'year')) {
            formattedDate = this.props.momentDisplayDate.format('MMM Do, YYYY');
        }

        return (
            <div className={styles['calendar-header']}>
                <div className={styles['header-date']}>{formattedDate}</div>
                <div className={styles['header-controls']}>
                    <div onClick={this.handleJumpToToday} className={styles['btn-today']}>
                        {moment().date()}
                    </div>
                    <div onClick={this.handleLeftControlClick} className={styles['control-left']}>
                        <ChevronLeftIcon />
                    </div>
                    <div onClick={this.handleRightControlClick} className={styles['control-right']}>
                        <ChevronRightIcon />
                    </div>
                </div>
            </div>
        );
    };

    handleJumpToToday = () => {
        const newDate = moment();

        this.handleDisplayDateChange(newDate);
    };

    handleLeftControlClick = () => {
        const newDate = this.props.momentDisplayDate.clone().subtract(1, 'weeks');

        this.handleDisplayDateChange(newDate);
    };

    handleRightControlClick = () => {
        const newDate = this.props.momentDisplayDate.clone().add(1, 'weeks');

        this.handleDisplayDateChange(newDate);
    };

    // @ts-expect-error
    handleDisplayDateChange = (momentDate: Moment) => {
        this.props.onDisplayDateChange(momentDate.valueOf());
    };

    // @ts-expect-error
    getTodosForDay = (momentDisplayDate: Moment) => {
        this.setState({
            isLoadingTodos: true,
            todosHasError: false,
        });

        const filters = {
            filter: {
                dueTime: momentDisplayDate.format('YYYY-MM-DD'),
            },
            limit: 50,
        } as const;

        $.ajax({
            url: '/rest/todos',
            dataType: 'json',
            data: filters,
        }).then(
            (json) => {
                const todos = jsonToModelPropArray(json.todos, json, 'todos');
                this.setState({
                    todos: todos,
                    isLoadingTodos: false,
                });
            },
            () => {
                this.setState({
                    isLoadingTodos: false,
                    todosHasError: true,
                });
            }
        );
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const SidebarCalendar = connector(SidebarCalendarComponent);
